// Iter-190 — Compensation dialog (CEO/GESTIONNAIRE only).
// Lets you set salaire_brut_mensuel + salaire_net (info) + cjm_override for a user.
// History is auto-tracked server-side. Live CJM preview computed via /api/users/{id}/cjm-preview.
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CheckCircle2, History, Loader2, Wallet, Trash2 } from "lucide-react";

const fmtMad = (n) =>
  n === null || n === undefined ? "—" : `${Number(n).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} MAD`;

export default function CompensationDialog({ user, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({
    employment_type: "SALARIE_MA",
    salaire_brut_mensuel: "",
    salaire_net_mensuel: "",
    cjm_override: "",
    effective_from: new Date().toISOString().slice(0, 10),
  });
  const [history, setHistory] = useState([]);
  const [deletingRow, setDeletingRow] = useState(null);

  const reloadHistory = async () => {
    try {
      const r = await api.get(`/users/${user.user_id}/compensation`);
      setHistory(r.data?.history || []);
    } catch { /* ignore */ }
  };

  const deleteHistoryRow = async (h) => {
    if (!window.confirm(`Supprimer la ligne du ${h.effective_from} (${h.salaire_brut_mensuel ? `brut ${h.salaire_brut_mensuel}` : `CJM ${h.cjm_override}`}) ? Les rapports financiers seront recalculés.`)) return;
    setDeletingRow(h);
    try {
      await api.post(`/users/${user.user_id}/compensation-history/delete`, {
        effective_from: h.effective_from,
        created_at: h.created_at || null,
      });
      toast.success("Ligne d'historique supprimée");
      await reloadHistory();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    } finally { setDeletingRow(null); }
  };
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Iter-236 — dynamic brut ↔ net (Maroc: CNSS/AMO salarial + IR configurables).
  const [lastEdited, setLastEdited] = useState(null); // "brut" | "net"
  const [payrollSim, setPayrollSim] = useState(null);

  useEffect(() => {
    if (!open || form.employment_type === "FREELANCE" || !lastEdited) return;
    const raw = lastEdited === "brut" ? form.salaire_brut_mensuel : form.salaire_net_mensuel;
    const val = parseFloat(raw);
    if (!raw || isNaN(val) || val <= 0) { setPayrollSim(null); return; }
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const q = lastEdited === "brut" ? `brut=${val}` : `net=${val}`;
        const r = await api.get(`/payroll/simulate?${q}`);
        if (cancel) return;
        setPayrollSim(r.data);
        if (lastEdited === "brut") {
          setForm((f) => ({ ...f, salaire_net_mensuel: String(r.data.net) }));
        } else {
          setForm((f) => ({ ...f, salaire_brut_mensuel: String(r.data.brut) }));
        }
      } catch { /* ignore */ }
    }, 450);
    return () => { cancel = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.employment_type, lastEdited, lastEdited === "brut" ? form.salaire_brut_mensuel : form.salaire_net_mensuel]);

  useEffect(() => {
    if (!open || !user?.user_id) return;
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get(`/users/${user.user_id}/compensation`);
        if (cancel) return;
        const cur = r.data?.current || {};
        setForm({
          employment_type: cur.employment_type || "SALARIE_MA",
          salaire_brut_mensuel: cur.salaire_brut_mensuel || "",
          salaire_net_mensuel: cur.salaire_net_mensuel || "",
          cjm_override: cur.cjm_override || "",
          effective_from: new Date().toISOString().slice(0, 10),
        });
        setHistory(r.data?.history || []);
        const p = await api.get(`/users/${user.user_id}/cjm-preview`);
        if (cancel) return;
        setPreview(p.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Erreur de chargement");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, user?.user_id]);

  // Iter-191c — live what-if preview: refresh CJM whenever the form fields change
  // (debounced 300ms). Hits /cjm-preview with brut/override/type as query params so
  // the preview reflects the unsaved form values without persisting.
  useEffect(() => {
    if (!open || !user?.user_id || loading) return;
    const params = new URLSearchParams();
    params.set("employment_type", form.employment_type);
    if (form.salaire_brut_mensuel !== "") params.set("brut", String(parseFloat(form.salaire_brut_mensuel) || 0));
    if (form.cjm_override !== "") params.set("cjm_override", String(parseFloat(form.cjm_override) || 0));
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const p = await api.get(`/users/${user.user_id}/cjm-preview?${params.toString()}`);
        if (!cancel) setPreview(p.data);
      } catch { /* ignore */ }
    }, 300);
    return () => { cancel = true; clearTimeout(t); };
  }, [open, user?.user_id, loading, form.employment_type, form.salaire_brut_mensuel, form.cjm_override]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        employment_type: form.employment_type,
        salaire_brut_mensuel: form.salaire_brut_mensuel ? parseFloat(form.salaire_brut_mensuel) : null,
        salaire_net_mensuel: form.salaire_net_mensuel ? parseFloat(form.salaire_net_mensuel) : null,
        cjm_override: form.cjm_override ? parseFloat(form.cjm_override) : null,
        effective_from: form.effective_from || null,
      };
      await api.put(`/users/${user.user_id}/compensation`, payload);
      toast.success("Rémunération enregistrée");
      // Refresh preview after save
      try {
        const p = await api.get(`/users/${user.user_id}/cjm-preview`);
        setPreview(p.data);
      } catch { /* ignore */ }
      onSaved?.();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="compensation-dialog" className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet size={16} /> Rémunération · {user?.name}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-zinc-500 text-sm"><Loader2 size={16} className="animate-spin inline mr-2" />Chargement…</div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>Type d'emploi</Label>
              <Select
                value={form.employment_type}
                onValueChange={(v) => setForm({ ...form, employment_type: v })}
              >
                <SelectTrigger data-testid="compensation-employment-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SALARIE_MA">Salarié (Maroc)</SelectItem>
                  <SelectItem value="FREELANCE">Freelance (TJM HT déclaré dans la fiche)</SelectItem>
                  <SelectItem value="OTHER">Autre (CEO / Admin / Gestionnaire)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(form.employment_type === "SALARIE_MA" || form.employment_type === "OTHER") && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="comp-brut">Salaire brut mensuel{form.employment_type === "SALARIE_MA" && <span className="text-red-600"> *</span>}</Label>
                  <Input
                    id="comp-brut"
                    data-testid="compensation-brut"
                    type="number" min="0" step="100"
                    value={form.salaire_brut_mensuel}
                    onChange={(e) => { setLastEdited("brut"); setForm({ ...form, salaire_brut_mensuel: e.target.value }); }}
                  />
                </div>
                <div>
                  <Label htmlFor="comp-net">Salaire net mensuel <span className="text-zinc-400 text-[10px]">(calculé ↔ modifiable)</span></Label>
                  <Input
                    id="comp-net"
                    data-testid="compensation-net"
                    type="number" min="0" step="100"
                    value={form.salaire_net_mensuel}
                    onChange={(e) => { setLastEdited("net"); setForm({ ...form, salaire_net_mensuel: e.target.value }); }}
                  />
                </div>
                {form.employment_type !== "FREELANCE" && payrollSim && (
                  <div className="col-span-2 text-[11px] font-mono text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2" data-testid="compensation-payroll-breakdown">
                    IR : {fmtMad(payrollSim.ir)} · CNSS sal. : {fmtMad(payrollSim.cnss_salarial)} · AMO : {fmtMad(payrollSim.amo_salarial)}
                    <span className="text-zinc-400"> · </span>
                    Charges patronales : {fmtMad(payrollSim.employer_charges?.total)} · Coût cabinet : {fmtMad(payrollSim.employer_cost)}
                  </div>
                )}
              </div>
            )}

            {form.employment_type !== "FREELANCE" && (
              <div>
                <Label htmlFor="comp-override">CJM manuel <span className="text-zinc-400 text-[10px]">(forcer un CJM custom — prioritaire sur le brut)</span></Label>
                <Input
                  id="comp-override"
                  data-testid="compensation-override"
                  type="number" min="0" step="10"
                  value={form.cjm_override}
                  onChange={(e) => setForm({ ...form, cjm_override: e.target.value })}
                  placeholder="Vide = calcul automatique depuis le brut + CNSS"
                />
              </div>
            )}

            <div>
              <Label htmlFor="comp-eff">Effectif à partir du</Label>
              <Input
                id="comp-eff"
                data-testid="compensation-effective"
                type="date"
                value={form.effective_from}
                onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
              />
            </div>

            {preview && (
              <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3 text-xs space-y-1" data-testid="compensation-preview">
                <div className="font-semibold text-emerald-900">CJM aujourd'hui</div>
                <div className="font-mono text-emerald-900 text-lg" data-testid="compensation-preview-cjm">
                  {fmtMad(preview.cjm)} / jour
                </div>
                {(preview.employment_type === "SALARIE_MA" || preview.employment_type === "OTHER") && preview.brut_used > 0 && (
                  <div className="text-emerald-800 font-mono">
                    {fmtMad(preview.brut_used)} + {fmtMad(preview.cnss_full_charge)} (full)
                    + {fmtMad(preview.cnss_plafonne_charge)} (plafonné CNSS)
                    = {fmtMad(preview.monthly_cost)} / {preview.working_days} jours ouvrés
                  </div>
                )}
                {preview.source === "override" && preview.cjm > 0 && (
                  <div className="text-emerald-800 font-mono text-[10px]">
                    CJM manuel (override actif — formule ignorée)
                  </div>
                )}
                {preview.warning && <div className="text-amber-800">⚠ {preview.warning}</div>}
              </div>
            )}

            {history.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-zinc-600 inline-flex items-center gap-1"><History size={11} /> Historique ({history.length})</summary>
                <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
                <table className="mt-2 w-full font-mono text-[11px]">
                  <thead className="text-zinc-500">
                    <tr><th className="text-left py-1">Effectif</th><th className="text-left py-1">Brut</th><th className="text-left py-1">CJM Override</th><th className="py-1"></th></tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => (
                      <tr key={i} className="border-t border-zinc-100 group">
                        <td className="py-1">{h.effective_from}</td>
                        <td className="py-1">{fmtMad(h.salaire_brut_mensuel)}</td>
                        <td className="py-1">{fmtMad(h.cjm_override)}</td>
                        <td className="py-1 text-right">
                          <button
                            type="button"
                            onClick={() => deleteHistoryRow(h)}
                            disabled={!!deletingRow || history.length <= 1}
                            data-testid={`compensation-history-delete-${i}`}
                            title={history.length <= 1 ? "Impossible de supprimer la dernière ligne" : "Supprimer cette ligne erronée (recalcule les rapports)"}
                            className="text-zinc-300 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            {deletingRow === h ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </details>
            )}
          </div>
        )}
        <DialogFooter>
          <button onClick={() => onOpenChange?.(false)} disabled={saving} className="px-3 h-9 bg-white border border-zinc-300 text-sm rounded-md hover:bg-zinc-50">Annuler</button>
          <button
            onClick={save}
            disabled={saving || loading}
            data-testid="compensation-save"
            className="inline-flex items-center gap-1.5 px-3 h-9 bg-brand-navy text-white text-sm rounded-md hover:bg-brand-navy-600 disabled:opacity-60"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
            Enregistrer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
